<template>
  <div class="team-grid-section">
    
    <div class="team-grid-container">
      <div v-for="person in teamMembers" :key="person.id" class="grid-person-card">
        <router-link :to="{ name: 'PersonDetail', params: { id: person.id } }" class="grid-image-link">
          <div class="grid-person-img-wrapper">
            <img :src="getImagePath(person.image)" :alt="person.name" class="grid-person-img" />
            <div class="overlay">
              <span class="view-profile">{{ $t('common.ViewProfile') || 'View Profile' }}</span>
            </div>
          </div>
        </router-link>
        <div class="grid-person-info">
          <h3>{{ person.name }}</h3>
          <p class="grid-person-title">{{ person.title }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TeamGrid',
  // TeamGrid now accepts 'teamMembers' as a prop
  props: {
    teamMembers: {
      type: Array,
      required: true
    }
  },
  methods: {
    getImagePath(imageName) {
      // THIS IS THE CRITICAL CHANGE: Use require() for Webpack to process the image path
      try {
        // Assuming images are in src/assets/contact/lead/ as established earlier
        return require(`@/assets/contact/team/${imageName}`);
      } catch (e) {
        console.warn(`[TeamGrid] Image not found: ${imageName} in src/assets/contact/lead/. Using fallback.`, e);
        // Ensure you have a fallback image at '@/assets/logo.png'
        return require('@/assets/logo.png'); // Fallback image path
      }
    }
  }
};
</script>

<style scoped>
/*
  All the styles specific to the team grid and individual cards
  (e.g., .team-grid-section, .grid-title, .team-grid-container,
  .grid-person-card, .grid-image-link, .grid-person-img-wrapper,
  .grid-person-img, .overlay, .view-profile, .grid-person-info,
  .grid-person-title, and their respective media queries)
  should be placed here.
*/

.team-grid-section {
  padding: 60px 20px;
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
}

.grid-title {
  font-size: 2.5rem;
  color: #2c3e50;
  margin-bottom: 50px;
  position: relative;
  padding-bottom: 15px;
}

.grid-title::after {
  content: '';
  display: block;
  width: 100px;
  height: 4px;
  background-color: #bbd55a;
  margin: 15px auto 0;
  border-radius: 2px;
}

.team-grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); /* Responsive grid */
  gap: 30px; /* Space between grid items */
}

.grid-person-card {
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  text-align: center;
  display: flex;
  flex-direction: column;
}

.grid-person-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 25px rgba(0, 0, 0, 0.15);
}

.grid-image-link {
  display: block; /* Make the whole link block-level */
  text-decoration: none;
  color: inherit;
  position: relative;
  overflow: hidden;
  border-radius: 12px 12px 0 0; /* Match card top corners */
}

.grid-person-img-wrapper {
  width: 100%;
  padding-top: 100%; /* Creates a square container based on width */
  position: relative;
  overflow: hidden;
}

.grid-person-img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  transition: transform 0.4s ease;
}

.grid-image-link:hover .grid-person-img {
  transform: scale(1.05); /* Slight zoom on hover */
}

/* Overlay for "View Profile" text */
.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5); /* Semi-transparent black */
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none; /* Allows click-through to the image/link */
}

.grid-image-link:hover .overlay {
  opacity: 1;
}

.view-profile {
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 10px 20px;
  border: 2px solid #bbd55a;
  border-radius: 5px;
  transform: translateY(10px);
  opacity: 0;
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.grid-image-link:hover .view-profile {
  transform: translateY(0);
  opacity: 1;
}

.grid-person-info {
  padding: 20px 15px;
  flex-grow: 1; /* Allows info section to expand */
  display: flex;
  flex-direction: column;
  justify-content: center; /* Center content vertically */
}

.grid-person-info h3 {
  font-size: 1.4rem;
  color: #333;
  margin: 0 0 8px;
}

.grid-person-title {
  font-size: 0.95rem;
  color: #777;
  margin: 0;
}

/* Responsive adjustments for the grid */
@media (max-width: 768px) {
  .team-grid-container {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 20px;
  }
  .grid-title {
    font-size: 2rem;
    margin-bottom: 40px;
  }
  .grid-person-info h3 {
    font-size: 1.2rem;
  }
}

@media (max-width: 480px) {
  .team-grid-container {
    grid-template-columns: 1fr; /* Single column on very small screens */
    gap: 15px;
  }
  .grid-title {
    font-size: 1.8rem;
    margin-bottom: 30px;
  }
  .grid-person-info h3 {
    font-size: 1.1rem;
  }
  .grid-person-title {
    font-size: 0.85rem;
  }
}
</style>